/**
 * Safe expression evaluator - prevents arbitrary code execution
 * @param {string} expression - Expression to evaluate (e.g. '"Fisica" == "Fisica"')
 * @returns {boolean} - Evaluation result
 */
export const safeEval = (expression) => {
  if (typeof expression !== 'string') {
    throw new Error('Expression must be a string');
  }
  sanitizeExpression(expression);
  const tokens = tokenize(expression.trim());
  const { value, pos } = parseOr(tokens, 0);
  if (pos !== tokens.length) throw new Error('Unexpected tokens');
  return Boolean(value);
};

const sanitizeExpression = (expr) => {
  // Strip quoted string contents before validation so that field values
  // (accented chars, &, |, /, etc.) don't cause false rejects.
  // Quoted content is safe: the custom parser treats it as an opaque string value.
  const structural = expr.replace(/"[^"]*"|'[^']*'/g, '""');

  const allowedChars = /^[\s\w"'()=!&|.<>+-]*$/;
  if (!allowedChars.test(structural)) {
    throw new Error('Expression contains invalid characters');
  }

  const dangerousPatterns = [
    /\bfunction\s*\(/i,
    /\bconstructor\b/i,
    /\bprototype\b/i,
    /\beval\s*\(/i,
    /\bexec\s*\(/i,
    /\bsetTimeout\s*\(/i,
    /\bsetInterval\s*\(/i,
    /\brequire\s*\(/i,
    /\bimport\b/i,
    /window\./i,
    /document\./i,
    /process\./i,
    /global\./i,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(structural)) {
      throw new Error('Expression contains forbidden pattern');
    }
  }
};

// --- Tokenizer ---

const tokenize = (expr) => {
  const tokens = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = null;

  for (let i = 0; i < expr.length; i++) {
    const char = expr[i];
    const nextChar = expr[i + 1];

    if ((char === '"' || char === "'") && !inQuotes) {
      if (current) {
        tokens.push(current.trim());
        current = '';
      }
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      current += char;
      tokens.push(current);
      current = '';
      inQuotes = false;
      quoteChar = null;
    } else if (inQuotes) {
      current += char;
    } else if (char === '(' || char === ')') {
      if (current) {
        tokens.push(current.trim());
        current = '';
      }
      tokens.push(char);
    } else if ((char === '&' || char === '|') && nextChar === char) {
      if (current) {
        tokens.push(current.trim());
        current = '';
      }
      tokens.push(char + nextChar);
      i++;
    } else if (char === '=' || char === '!' || char === '<' || char === '>') {
      if (current && !['=', '!', '<', '>'].includes(current[current.length - 1])) {
        tokens.push(current.trim());
        current = '';
      }
      current += char;
      if ((char === '=' || char === '!' || char === '<' || char === '>') && nextChar === '=') {
        current += nextChar;
        i++;
        if ((char === '=' || char === '!') && expr[i + 1] === '=') {
          current += expr[i + 1];
          i++;
        }
      }
      tokens.push(current);
      current = '';
    } else if (char === ' ') {
      if (current) {
        tokens.push(current.trim());
        current = '';
      }
    } else {
      current += char;
    }
  }

  if (current) tokens.push(current.trim());
  return tokens.filter((t) => t.length > 0);
};

// --- Recursive descent parser (immutable, position-based) ---

const parseOr = (tokens, pos) => {
  let result = parseAnd(tokens, pos);
  while (result.pos < tokens.length && tokens[result.pos] === '||') {
    const right = parseAnd(tokens, result.pos + 1);
    result = { value: result.value || right.value, pos: right.pos };
  }
  return result;
};

const parseAnd = (tokens, pos) => {
  let result = parseComparison(tokens, pos);
  while (result.pos < tokens.length && tokens[result.pos] === '&&') {
    const right = parseComparison(tokens, result.pos + 1);
    result = { value: result.value && right.value, pos: right.pos };
  }
  return result;
};

const parseComparison = (tokens, pos) => {
  let result = parsePrimary(tokens, pos);
  const compOps = ['===', '!==', '==', '!=', '<=', '>=', '<', '>'];
  while (result.pos < tokens.length && compOps.includes(tokens[result.pos])) {
    const op = tokens[result.pos];
    const right = parsePrimary(tokens, result.pos + 1);
    result = { value: compare(result.value, right.value, op), pos: right.pos };
  }
  return result;
};

const parsePrimary = (tokens, pos) => {
  if (pos >= tokens.length) throw new Error('Unexpected end of expression');
  const token = tokens[pos];
  if (token === '(') {
    const inner = parseOr(tokens, pos + 1);
    if (inner.pos >= tokens.length || tokens[inner.pos] !== ')') {
      throw new Error('Missing closing parenthesis');
    }
    return { value: inner.value, pos: inner.pos + 1 };
  }
  return { value: parseValue(token), pos: pos + 1 };
};

const parseValue = (token) => {
  if (token === 'true') return true;
  if (token === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(token)) return Number(token);
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    return token.slice(1, -1);
  }
  return token;
};

const compare = (left, right, operator) => {
  switch (operator) {
    case '==':
      return left == right;
    case '===':
      return left === right;
    case '!=':
      return left != right;
    case '!==':
      return left !== right;
    case '<':
      return left < right;
    case '>':
      return left > right;
    case '<=':
      return left <= right;
    case '>=':
      return left >= right;
    default:
      throw new Error(`Unknown operator: ${operator}`);
  }
};

export default safeEval;
